// MessageTurn.tsx — one turn of the Sessions chat transcript: a user
// bubble, an assistant reply (with its collapsible thinking trace), a
// failed-turn card, or a tool call with its evidence card and live UI.
// Split out of Sessions.tsx; the rendering rules are documented on the
// component below.
import React, { useState, useEffect, useRef } from 'react';
import { Spinner } from 'react-bootstrap';
import { BsExclamationTriangleFill } from 'react-icons/bs';
import { InlineAccordion, Markdown } from 'common';
import LiveMarkdown from './live/LiveMarkdown';
import ToolEvidence from './ToolEvidence';
import { splitEmbeddedImages } from './chatState';
import type { SessionMessage } from '../api';

// MessageTurn renders one chat turn, full-pane width. Who said it is
// carried by alignment alone — user right, assistant left. No role
// caption (redundant, and it cost a line above every message) and no
// background tint (a colored surface under body text, which C-MSG-1
// forbids and which made the transcript a stack of boxes rather than
// something you can read top to bottom).
//
// Assistant content is markdown;
// user content is plain text (no need to render markdown for what they
// just typed — and it'd be confusing if backticks suddenly rendered).
// Assistant turns with a non-empty Qwen3-style thinking trace surface it
// as an autocollapsing block above the visible reply:
//   - while the model is thinking (reasoning present, no content yet),
//     the block is open so the user can watch the trace stream in;
//   - the moment the first content delta arrives, the block auto-collapses
//     so the visible reply isn't pushed off-screen;
//   - a newer thinking block starting supersedes this one, which closes
//     too: a turn that thinks between tool calls has a run of messages
//     with reasoning and no content, and without this every one of them
//     stayed open. Not while the user is engaged with it — pointer over
//     it, keyboard focus or a text selection inside — the close waits
//     until they leave;
//   - after the user toggles it, expand/collapse is purely user-driven
//     (the click handler stamps `touched` so neither auto-close fights
//     the user).
//
// Historical messages loaded from the store land here with content already
// non-empty, so the open-while-thinking branch never fires and they start
// (and stay) collapsed until the user expands them.
//
// A turn carrying `error` is the harness reporting a failed request rather
// than the model replying, and renders as an outlined card with a
// "Turn failed" eyebrow and verbatim body — see `isError` below.
//
// `busy` marks the turn the model is still generating, and puts a spinner
// in the turn's top-right gutter. It answers a question the streaming
// text cannot: a model that is thinking, running a tool, or waiting on a
// slow first token produces no visible output for seconds at a time, and
// still text is indistinguishable from a wedged request. The gutter is
// deliberately outside the prose — it must not move as the reply grows.
//
// No bubble. The transcript is one continuous document: a turn draws no
// border, no fill and no rounded box of its own, so a long reply reads as
// prose on the page rather than as a paragraph trapped in a container.
// Cards are reserved for the things that genuinely are cards — a tool's
// live UI, and a failed turn. Role is carried by alignment alone: the
// user's words sit right, the model's left.
/**
 * Whether the user is engaged with a thinking block: pointer over it,
 * keyboard focus inside it, or a text selection that starts or ends in it.
 */
function useThinkingEngagement() {
  const ref = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState(false);
  const [focus, setFocus] = useState(false);
  const [selecting, setSelecting] = useState(false);
  useEffect(() => {
    const onSelection = () => {
      const el = ref.current;
      const sel = document.getSelection();
      setSelecting(!!el && !!sel && !sel.isCollapsed
        && (el.contains(sel.anchorNode) || el.contains(sel.focusNode)));
    };
    document.addEventListener('selectionchange', onSelection);
    return () => document.removeEventListener('selectionchange', onSelection);
  }, []);
  return {
    ref,
    active: hover || focus || selecting,
    handlers: {
      onPointerEnter: () => setHover(true),
      onPointerLeave: () => setHover(false),
      onFocus: () => setFocus(true),
      onBlur: (e: React.FocusEvent<HTMLDivElement>) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocus(false);
      },
    },
  };
}

const MessageTurn: React.FC<{
  msg: SessionMessage;
  sessionId: string;
  busy?: boolean;
  /** Space above this turn — see turnGapRem in chatState.ts. */
  gapRem?: number;
  /** A later message has its own thinking trace — see latestReasoningIndex. */
  thinkingSuperseded?: boolean;
}> = ({ msg, sessionId, busy = false, gapRem = 0, thinkingSuperseded = false }) => {
  const isUser = msg.role === 'user';
  const toolUi = msg.tool_ui ?? '';
  // A turn that records a tool call rather than something anyone said.
  // `tool_name` is the marker: it is set live by tool_started and
  // restored from history, whereas tool_ui is only present for the
  // minority of tools that draw one.
  const isToolCall = !isUser && !!msg.tool_name;
  // Failure turns (upstream 4xx/5xx, iteration cap) get their own skin
  // rather than the assistant one. Three reasons they can't share it: a
  // raw upstream payload is JSON and URLs, so the markdown renderer eats
  // the punctuation that makes it diagnosable; styled as a normal reply it
  // reads as the model having answered with a stack trace; and the fact
  // the turn failed at all is what the user needs to spot first.
  const isError = !isUser && (msg.error ?? false);
  const reasoning = msg.reasoning ?? '';
  const hasReasoning = !isUser && reasoning.length > 0;
  const isThinkingLive = hasReasoning && !msg.content && !thinkingSuperseded;
  const [open, setOpen] = useState(isThinkingLive);
  const touched = useRef(false);
  const engaged = useThinkingEngagement();
  useEffect(() => {
    if (touched.current) return;
    // Closing under the user's pointer or selection would yank the text
    // they are reading; the effect re-runs when they leave.
    if (!isThinkingLive && engaged.active) return;
    setOpen(isThinkingLive);
  }, [isThinkingLive, engaged.active]);
  const toggle = () => { touched.current = true; setOpen((v) => !v); };
  return (
    <div
      className="position-relative"
      style={{
        // No box: no padding, no border, no fill, no radius. The turn is a
        // block in a document, and its only chrome is the gutter the
        // spinner lives in. That gutter is reserved on every model turn,
        // not only while one is running — sizing it on `busy` would reflow
        // the whole reply the moment the turn ended, which is the one
        // moment the user is reading it.
        paddingRight: isUser ? undefined : '2.5rem',
        marginTop: gapRem ? `${gapRem}rem` : undefined,
        // Role is carried by alignment plus, for the user only, a bubble.
        // The model's turn keeps no container of its own — its reply, tool
        // cards and live components read as one continuous piece of work,
        // and a border around all of that would frame most of the screen.
        width: '100%',
        textAlign: isUser ? 'right' : 'left',
        overflowWrap: 'anywhere',
      }}
    >
      {busy && (
        <div
          className="position-absolute text-muted"
          style={{ top: 0, right: '0.5rem', lineHeight: 1 }}
          title="Working…"
        >
          {/* The graphic is decorative; the live region beside it is what
              a screen reader gets, since the reply streams in as ordinary
              text with no cue that more is coming. */}
          <Spinner animation="border" size="sm" aria-hidden="true" />
          <span className="visually-hidden" role="status" aria-live="polite">
            Working…
          </span>
        </div>
      )}
      {hasReasoning && (
        <div ref={engaged.ref} {...engaged.handlers}>
          <InlineAccordion
            // Space only when something follows inside this turn; a
            // thinking-only step is a collapsed row, spaced by turnGapRem.
            className={msg.content || toolUi || isError ? 'mb-1' : undefined}
            open={open}
            onToggle={toggle}
            header={<>Thinking{isThinkingLive ? <span className="thinking-ellipsis" /> : null}</>}
            bodyStyle={{ whiteSpace: 'normal' }}
          >
            <Markdown>{reasoning}</Markdown>
          </InlineAccordion>
        </div>
      )}
      {isError ? (
        // A failure is one of the two things in the transcript that still
        // earns a card: it is not part of the conversation, it interrupts
        // it. Outlined per C-MSG-1 — the danger color is in the border and
        // the eyebrow, never behind the payload text.
        <div
          style={{
            border: '1px solid rgba(var(--bs-danger-rgb, 220, 53, 69), 0.45)',
            borderLeft: '3px solid var(--bs-danger)',
            borderRadius: 'var(--radius-sm, 0.25rem)',
            padding: '0.75rem 1rem',
            // The card is a block of diagnostics, so it reads left even
            // though the surrounding turn may not.
            textAlign: 'left',
          }}
        >
          <div
            className="text-uppercase mb-1 d-inline-flex align-items-center gap-1 text-danger fw-semibold"
            style={{ fontSize: '0.65rem', letterSpacing: '0.05em' }}
          >
            <BsExclamationTriangleFill /> Turn failed
          </div>
          {/* Verbatim, outside markdown-content: the payload is an upstream
              error body, and rendering it as markdown would swallow the
              underscores, braces and asterisks that make it readable. */}
          <div style={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}>
            {msg.content}
          </div>
        </div>
      ) : (
        <div className="markdown-content">
          {isUser ? (
            // The user's turn is a bubble; the model's is not. The
            // asymmetry is the point: what you said is a discrete,
            // finished object, and a container that hugs it says so. What
            // the model said is the body of the page — the reply, its
            // tool cards and any live components are one continuous piece
            // of work, and boxing that would put a frame around most of
            // the screen.
            //
            // inline-block so the bubble is the width of the words rather
            // than the width of the pane, and left-aligned inside even
            // though the turn as a whole sits right — a right-ragged
            // paragraph is hard to read.
            //
            // The body is still verbatim — pre-wrap, no markdown pass, so
            // the paths and command lines people type survive. The one
            // exception is an embedded image: the composer renders a
            // pasted screenshot as a picture, and printing its base64 back
            // here shows the user something they never wrote. See
            // splitEmbeddedImages.
            <div
              style={{
                display: 'inline-block',
                maxWidth: '85%',
                textAlign: 'left',
                whiteSpace: 'pre-wrap',
                background: 'var(--bs-secondary-bg)',
                border: '1px solid var(--bs-border-color)',
                borderRadius: 'var(--radius-default, 0.375rem)',
                padding: '0.5rem 0.75rem',
              }}
            >
              {splitEmbeddedImages(msg.content).map((part, i) =>
                part.kind === 'image' ? (
                  <img
                    key={i}
                    src={part.src}
                    alt={part.alt}
                    style={{
                      display: 'block',
                      maxWidth: '100%',
                      height: 'auto',
                      margin: '0.25rem 0',
                      borderRadius: 'var(--radius-sm, 0.25rem)',
                      border: '1px solid var(--bs-border-color)',
                    }}
                  />
                ) : (
                  // A fragment rather than a wrapper element: the run is a
                  // bare text node under the bubble, exactly as it was
                  // before the split, so it inherits pre-wrap unchanged.
                  <React.Fragment key={i}>{part.text}</React.Fragment>
                ),
              )}
            </div>
          ) : (
            // A turn that is only a tool card or a UI has no prose of its
            // own — the "…" placeholder would read as the model still
            // thinking, when in fact the work is rendered below.
            //
            // allowDataImages because a tool can answer with a picture — a
            // screenshot, a rendered chart — and inlining it is the only
            // way to return one from a pod with no URL of its own. Without
            // the opt-in react-markdown strips the `data:` URI and the turn
            // renders as a broken image.
            <Markdown allowDataImages>
              {msg.content || (hasReasoning || toolUi || isToolCall ? '' : '…')}
            </Markdown>
          )}
        </div>
      )}
      {isToolCall && (
        <ToolEvidence
          name={msg.tool_name || 'tool'}
          input={msg.tool_input}
          output={msg.tool_output}
          isError={msg.tool_error}
          running={msg.tool_running}
        />
      )}
      {/* Below the evidence card, not instead of it: the UI is what the
          tool wanted to say, the card is what it actually did. */}
      {toolUi && (
        <LiveMarkdown
          markdown={toolUi}
          sessionId={sessionId}
          toolCallId={msg.tool_call_id ?? ''}
        />
      )}
    </div>
  );
};

export default MessageTurn;
